/**
 * pbn.ts
 *
 * Jan Wolter's XML format for paint-by-number puzzles, version 0.3
 * (https://webpbn.com/pbn_fmt.html): reading a file of one or more puzzles,
 * and writing one. Works in the browser and in Node (no DOM needed).
 *
 * Only grid puzzles are read. A puzzle the game can't play (a triddler,
 * blotted clues, colors split into triangles, a goal picture with undecided
 * cells) is skipped with the reason, and the rest of the file is still read.
 */
import { cluesFromSolution } from './solution';
import type { Clue } from './types';

/** Largest side a puzzle may have (rows or columns). */
export const PBN_MAX_SIDE = 200;
/** Most ink colors a puzzle may use (the background not counted). */
export const PBN_MAX_COLORS = 64;
/** Longest file (in characters) the admin page uploads. */
export const PBN_MAX_FILE_LENGTH = 1_500_000;
/** Most puzzles read from one file. */
export const PBN_MAX_PUZZLES = 2000;

export const PBN_DOCTYPE = '<!DOCTYPE pbn SYSTEM "https://webpbn.com/pbn-0.3.dtd">';

export interface PbnColor {
  name: string;
  /** '#rrggbb', lower case. */
  hex: string;
  /** Its character in pictures; missing when the file gave none. */
  char?: string;
}

/** One puzzle read from a file, in the game's terms. */
export interface PbnPuzzle {
  /** Position in the file, from 0. */
  index: number;
  source?: string;
  id?: string;
  title?: string;
  author?: string;
  authorId?: string;
  copyright?: string;
  description?: string;
  note?: string;
  rows: number;
  columns: number;
  /** The background, then the inks the puzzle uses (a puzzle's `colors`). */
  colors: PbnColor[];
  rowClues: Clue[][];
  columnClues: Clue[][];
  /** The goal picture (0 background, k is colors[k]); null when the file gives only clues. */
  solution: number[] | null;
}

export interface PbnSkipped {
  index: number;
  title?: string;
  error: string;
}

export interface PbnFile {
  title?: string;
  source?: string;
  author?: string;
  authorId?: string;
  copyright?: string;
  note?: string;
  puzzles: PbnPuzzle[];
  /** Puzzles that couldn't be read, and why. */
  skipped: PbnSkipped[];
}

export interface PbnReadOptions {
  /**
   * When a puzzle's clues disagree with its goal picture, take the picture's
   * clues instead of skipping it (for rewriting a file after its pictures
   * were edited by hand).
   */
  ignoreClues?: boolean;
}

/** What importing a PBN file into the catalog did (`POST /api/admin/puzzles/import-pbn`). */
export interface PbnImportResult {
  /** Puzzles added to the catalog; a warning says what to look at. */
  imported: Array<{ index: number; id: string; name: string; warning?: string }>;
  /** Puzzles the catalog already has from an earlier import. */
  existing: Array<{ index: number; id: string; name: string }>;
  /** Puzzles that couldn't be read or solved. */
  failed: Array<{ index: number; name?: string; error: string }>;
}

/** The file isn't one this reader can make sense of, or a puzzle in it isn't. */
export class PbnError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PbnError';
  }
}

// ===== Reading =====

/** Reads every puzzle in a PBN file. Throws `PbnError` when it isn't a PBN file at all. */
export function parsePbn(xml: string, options: PbnReadOptions = {}): PbnFile {
  const root = parseXml(xml);
  const set = findTop(root, 'puzzleset');
  const puzzleElements = set ? childElements(set, 'puzzle') : [findTop(root, 'puzzle')].filter((el): el is XmlElement => Boolean(el));
  if (!set && puzzleElements.length === 0) throw new PbnError("No <puzzleset> or <puzzle> in the file: it doesn't look like a PBN file");
  if (puzzleElements.length === 0) throw new PbnError('The file has no puzzles');
  if (puzzleElements.length > PBN_MAX_PUZZLES) throw new PbnError(`The file has ${puzzleElements.length} puzzles; at most ${PBN_MAX_PUZZLES} can be read at once`);

  const file: PbnFile = { puzzles: [], skipped: [] };
  if (set) {
    assign(file, 'title', line(childText(set, 'title')));
    assign(file, 'source', line(childText(set, 'source')));
    assign(file, 'author', line(childText(set, 'author')));
    assign(file, 'authorId', line(childText(set, 'authorid')));
    assign(file, 'copyright', line(childText(set, 'copyright')));
    assign(file, 'note', paragraph(childText(set, 'note')));
  }
  puzzleElements.forEach((element, index) => {
    try {
      file.puzzles.push(readPuzzle(element, index, file, options));
    } catch (err) {
      if (!(err instanceof PbnError)) throw err;
      const skipped: PbnSkipped = { index, error: err.message };
      assign(skipped, 'title', line(childText(element, 'title')));
      file.skipped.push(skipped);
    }
  });
  return file;
}

const PREDEFINED: PbnColor[] = [
  { name: 'black', hex: '#000000', char: 'X' },
  { name: 'white', hex: '#ffffff', char: '.' },
];

type NamedCount = { name: string; amount: number };

function readPuzzle(element: XmlElement, index: number, defaults: PbnFile, options: PbnReadOptions): PbnPuzzle {
  const type = (element.attrs.type ?? 'grid').trim().toLowerCase();
  if (type !== 'grid') throw new PbnError(`"${type}" puzzles aren't supported, only grids`);

  // Colors: the two predefined ones, which a <color> may redefine, then the declared ones in order.
  const declared: PbnColor[] = [];
  for (const colorElement of childElements(element, 'color')) {
    const name = (colorElement.attrs.name ?? '').trim();
    if (!name) throw new PbnError('A <color> has no name');
    const value = textOf(colorElement).trim();
    if (/[\\/]/.test(value)) throw new PbnError(`Color "${name}" is split into triangles, which isn't supported`);
    const hex = normalizeHex(value);
    if (!hex) throw new PbnError(`Color "${name}" has a value that isn't a hex color: "${value}"`);
    const char = colorElement.attrs.char;
    if (char !== undefined && !isPictureChar(char)) throw new PbnError(`Color "${name}" has a character that can't be used: "${char}"`);
    const color: PbnColor = { name, hex };
    if (char !== undefined) color.char = char;
    const existing = declared.findIndex((c) => sameName(c.name, name));
    if (existing >= 0) declared[existing] = color;
    else declared.push(color);
  }
  const all = [...PREDEFINED.filter((p) => !declared.some((c) => sameName(c.name, p.name))), ...declared];
  const colorNamed = (name: string, what: string): PbnColor => {
    const color = all.find((c) => sameName(c.name, name));
    if (!color) throw new PbnError(`${what} uses the color "${name}", which isn't defined`);
    return color;
  };
  const background = colorNamed(element.attrs.backgroundcolor ?? 'white', 'The background');
  const defaultColor = element.attrs.defaultcolor ?? 'black';

  // Characters for pictures: a declared color's wins over a predefined one's.
  const byChar = new Map<string, PbnColor>();
  for (const color of all) {
    if (color.char === undefined) continue;
    const other = byChar.get(color.char);
    if (other && declared.includes(other)) {
      if (declared.includes(color)) throw new PbnError(`Colors "${other.name}" and "${color.name}" use the same character "${color.char}"`);
      continue;
    }
    byChar.set(color.char, color);
  }

  const clueSets = descendants(element, 'clues');
  const rowSet = clueSets.find((el) => (el.attrs.type ?? '').trim().toLowerCase() === 'rows');
  const columnSet = clueSets.find((el) => (el.attrs.type ?? '').trim().toLowerCase() === 'columns');
  if (Boolean(rowSet) !== Boolean(columnSet)) throw new PbnError(`The puzzle has ${rowSet ? 'row' : 'column'} clues but no ${rowSet ? 'column' : 'row'} clues`);
  const readClues = (set: XmlElement, what: string): NamedCount[][] => childElements(set, 'line').map((lineElement, lineIndex) =>
    childElements(lineElement, 'count').map((count) => {
      const text = textOf(count).trim();
      if (!/^\d+$/.test(text)) throw new PbnError(`${what} ${lineIndex + 1} has a count that isn't a number: "${text}"`);
      const amount = Number(text);
      if (amount === 0) throw new PbnError('The puzzle has blotted clues (0), which aren\'t supported');
      const name = colorNamed(count.attrs.color ?? defaultColor, `${what} ${lineIndex + 1}`).name;
      if (sameName(name, background.name)) throw new PbnError(`${what} ${lineIndex + 1} has a clue in the background color`);
      return { name, amount };
    }));
  const namedRows = rowSet ? readClues(rowSet, 'Row') : null;
  const namedColumns = columnSet ? readClues(columnSet, 'Column') : null;

  const solutions = descendants(element, 'solution');
  const goal = solutions.find((s) => (s.attrs.type ?? 'goal').trim().toLowerCase() === 'goal')
    ?? solutions.find((s) => (s.attrs.type ?? '').trim().toLowerCase() === 'solution');
  const imageElement = goal ? descendants(goal, 'image')[0] : undefined;
  const picture = imageElement ? readPicture(textOf(imageElement), byChar) : null;

  if (!namedRows && !picture) throw new PbnError('The puzzle has neither clues nor a goal picture');
  const rows = namedRows?.length ?? picture!.length;
  const columns = namedColumns?.length ?? picture![0].length;
  if (picture && (picture.length !== rows || picture[0].length !== columns)) {
    throw new PbnError(`The goal picture is ${picture.length}×${picture[0].length} but the clues are for ${rows}×${columns}`);
  }
  if (rows === 0 || columns === 0) throw new PbnError('The puzzle has no rows or no columns');
  if (rows > PBN_MAX_SIDE || columns > PBN_MAX_SIDE) throw new PbnError(`The puzzle is ${rows}×${columns}; at most ${PBN_MAX_SIDE} per side is supported`);

  // Inks: the colors used, in the order they're defined.
  const used = new Set<string>();
  for (const line of [...(namedRows ?? []), ...(namedColumns ?? [])]) for (const count of line) used.add(count.name.toLowerCase());
  if (picture) for (const row of picture) for (const color of row) used.add(color.name.toLowerCase());
  const inks = all.filter((c) => used.has(c.name.toLowerCase()) && !sameName(c.name, background.name));
  if (inks.length > PBN_MAX_COLORS) throw new PbnError(`The puzzle uses ${inks.length} colors; at most ${PBN_MAX_COLORS} are supported`);
  const colors = [background, ...inks].map((c) => ({ ...c }));
  const colorIndex = (name: string) => colors.findIndex((c) => sameName(c.name, name));
  const toClues = (named: NamedCount[][]) => named.map((line) => line.map((count) => ({ colorIdx: colorIndex(count.name), amount: count.amount })));

  let rowClues = namedRows ? toClues(namedRows) : null;
  let columnClues = namedColumns ? toClues(namedColumns) : null;
  const solution = picture ? picture.flatMap((row) => row.map((color) => colorIndex(color.name))) : null;
  if (solution) {
    const derived = cluesFromSolution(solution, rows, columns);
    const matches = rowClues && sameClues(rowClues, derived.rowClues) && sameClues(columnClues!, derived.columnClues);
    if (rowClues && !matches && !options.ignoreClues) throw new PbnError("The clues don't match the goal picture");
    rowClues = derived.rowClues;
    columnClues = derived.columnClues;
  } else {
    checkCluesFit(rowClues!, columns, 'Row');
    checkCluesFit(columnClues!, rows, 'Column');
  }

  const puzzle: PbnPuzzle = { index, rows, columns, colors, rowClues: rowClues!, columnClues: columnClues!, solution };
  assign(puzzle, 'source', line(childText(element, 'source')) ?? defaults.source);
  assign(puzzle, 'id', line(childText(element, 'id')));
  assign(puzzle, 'title', line(childText(element, 'title')));
  assign(puzzle, 'author', line(childText(element, 'author')) ?? defaults.author);
  assign(puzzle, 'authorId', line(childText(element, 'authorid')) ?? defaults.authorId);
  assign(puzzle, 'copyright', line(childText(element, 'copyright')) ?? defaults.copyright);
  assign(puzzle, 'description', paragraph(childText(element, 'description')));
  assign(puzzle, 'note', paragraph(childText(element, 'note')));
  return puzzle;
}

/** A picture's rows, `|…|` each, as colors (white space ignored). */
function readPicture(text: string, byChar: Map<string, PbnColor>): PbnColor[][] {
  const rows: PbnColor[][] = [];
  let current: PbnColor[] | null = null;
  for (const ch of text.replace(/\s+/g, '')) {
    if (ch === '|') {
      if (current) rows.push(current);
      current = current ? null : [];
    } else if (ch === '/' || ch === '\\') {
      throw new PbnError('The goal picture is a triddler\'s, which isn\'t supported');
    } else if (!current) {
      throw new PbnError('The goal picture has cells outside a |…| row');
    } else if (ch === '?' || ch === '[' || ch === ']') {
      throw new PbnError('The goal picture has undecided cells (? or […])');
    } else {
      const color = byChar.get(ch);
      if (!color) throw new PbnError(`The goal picture uses "${ch}", which no color has`);
      current.push(color);
    }
  }
  if (current) throw new PbnError('A row of the goal picture isn\'t closed with |');
  if (rows.length === 0) throw new PbnError('The goal picture is empty');
  if (rows.some((row) => row.length !== rows[0].length)) throw new PbnError('The goal picture\'s rows aren\'t all the same length');
  return rows;
}

function sameClues(a: Clue[][], b: Clue[][]): boolean {
  return a.length === b.length && a.every((line, i) => line.length === b[i].length
    && line.every((clue, j) => clue.colorIdx === b[i][j].colorIdx && clue.amount === b[i][j].amount));
}

/** Blocks of one color need a gap between them; different colors don't. */
function checkCluesFit(lines: Clue[][], length: number, what: string): void {
  lines.forEach((line, i) => {
    const needed = line.reduce((sum, clue, j) => sum + clue.amount + (j > 0 && line[j - 1].colorIdx === clue.colorIdx ? 1 : 0), 0);
    if (needed > length) throw new PbnError(`${what} ${i + 1}'s clues don't fit in ${length} cells`);
  });
}

function normalizeHex(value: string): string | null {
  const hex = value.replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(hex)) return `#${[...hex].map((c) => c + c).join('')}`;
  if (/^[0-9a-f]{6}$/.test(hex)) return `#${hex}`;
  return null;
}

function isPictureChar(char: string): boolean {
  return char.length === 1 && !/[\s\\/|?[\]]/.test(char);
}

function sameName(a: string, b: string): boolean {
  return a.toLowerCase() === b.toLowerCase();
}

/** Sets `key` only when there's a value, so absent fields stay absent. */
function assign<T extends object, K extends keyof T>(target: T, key: K, value: T[K] | undefined): void {
  if (value !== undefined) target[key] = value;
}

/** One line of text: white space collapsed; undefined when empty. */
function line(text: string | undefined): string | undefined {
  const value = text?.replace(/\s+/g, ' ').trim();
  return value || undefined;
}

/** Text that may have lines: each line's spacing collapsed, blank lines at the ends dropped. */
function paragraph(text: string | undefined): string | undefined {
  const value = text?.split('\n').map((l) => l.replace(/\s+/g, ' ').trim()).join('\n').replace(/\n{3,}/g, '\n\n').trim();
  return value || undefined;
}

// ===== Writing =====

export type PbnColorInput = string | { hex: string; name?: string; char?: string };

export interface PbnWritePuzzle {
  source?: string;
  id?: string;
  title?: string;
  author?: string;
  authorId?: string;
  copyright?: string;
  description?: string;
  note?: string;
  rows: number;
  columns: number;
  /** The background first, then the inks: hex colors, or with a name and picture character. */
  colors: PbnColorInput[];
  /** The goal picture (0 background, k is colors[k]). */
  solution: number[];
}

export interface PbnWriteSet {
  source?: string;
  title?: string;
  author?: string;
  authorId?: string;
  copyright?: string;
  note?: string;
  puzzles: PbnWritePuzzle[];
}

/** Characters handed out to colors that don't bring their own (no XML specials). */
const CHAR_POOL = 'XABCDEFGHIJKLMNOPQRSTUVWYZabcdefghijklmnopqrstuvwxyz0123456789#%*+=@$!^~-:;,_()';

/** Writes puzzles as a PBN file: the clues and the goal picture of each. */
export function writePbn(set: PbnWriteSet): string {
  const out = ['<?xml version="1.0"?>', PBN_DOCTYPE, '', '<puzzleset>'];
  for (const [tag, value] of [['source', set.source], ['title', set.title], ['author', set.author], ['authorid', set.authorId], ['copyright', set.copyright]] as const) {
    if (value) out.push(element(tag, value));
  }
  for (const puzzle of set.puzzles) out.push('', ...writePuzzle(puzzle));
  if (set.note) out.push('', element('note', set.note));
  out.push('</puzzleset>', '');
  return out.join('\n');
}

function writePuzzle(puzzle: PbnWritePuzzle): string[] {
  const { rows, columns, solution } = puzzle;
  if (solution.length !== rows * columns) throw new PbnError(`The picture has ${solution.length} cells, expected ${rows}×${columns}`);
  const colors = nameColors(puzzle.colors);
  const outside = solution.findIndex((value) => !Number.isInteger(value) || value < 0 || value >= colors.length);
  if (outside >= 0) {
    throw new PbnError(`Cell ${Math.floor(outside / columns) + 1},${(outside % columns) + 1} has color ${solution[outside]}, which isn't in the palette`);
  }
  // The ink with the most cells is the default, so most counts need no color.
  const cellCounts = new Array(colors.length).fill(0);
  for (const value of solution) cellCounts[value]++;
  let defaultIndex = colors.length > 1 ? 1 : 0;
  for (let k = 2; k < colors.length; k++) if (cellCounts[k] > cellCounts[defaultIndex]) defaultIndex = k;
  const background = colors[0];
  const defaultName = defaultIndex > 0 ? colors[defaultIndex].name : 'black';
  const out = [`<puzzle type="grid" defaultcolor="${escape(defaultName)}" backgroundcolor="${escape(background.name)}">`];
  for (const [tag, value] of [['source', puzzle.source], ['id', puzzle.id], ['title', puzzle.title], ['author', puzzle.author],
    ['authorid', puzzle.authorId], ['copyright', puzzle.copyright], ['description', puzzle.description]] as const) {
    if (value) out.push(element(tag, value));
  }
  out.push('');
  for (const color of colors) out.push(`<color name="${escape(color.name)}" char="${escape(color.char!)}">${color.hex.slice(1)}</color>`);

  const { rowClues, columnClues } = cluesFromSolution(solution, rows, columns);
  const writeClues = (type: string, lines: Clue[][]) => {
    out.push('', `<clues type="${type}">`);
    for (const lineClues of lines) {
      out.push(`<line>${lineClues.map((clue) => clue.colorIdx === defaultIndex
        ? `<count>${clue.amount}</count>`
        : `<count color="${escape(colors[clue.colorIdx].name)}">${clue.amount}</count>`).join('')}</line>`);
    }
    out.push('</clues>');
  };
  writeClues('columns', columnClues);
  writeClues('rows', rowClues);

  out.push('', '<solution type="goal">', '<image>');
  for (let r = 0; r < rows; r++) {
    let row = '|';
    for (let c = 0; c < columns; c++) {
      row += colors[solution[r * columns + c]].char;
    }
    out.push(`${row}|`);
  }
  out.push('</image>', '</solution>');
  if (puzzle.note) out.push(element('note', puzzle.note));
  out.push('</puzzle>');
  return out;
}

/** Every color with a unique name and picture character, keeping the ones given when they're usable. */
function nameColors(input: PbnColorInput[]): Array<Required<PbnColor>> {
  if (input.length === 0) throw new PbnError('A puzzle needs at least a background color');
  const colors = input.map((c) => (typeof c === 'string' ? { hex: c } : c)).map((c) => {
    const hex = normalizeHex(c.hex);
    if (!hex) throw new PbnError(`"${c.hex}" isn't a hex color`);
    return { ...c, hex };
  });
  const names = new Set<string>();
  const chars = new Set<string>();
  const result = colors.map(() => ({ name: '', hex: '', char: '' }));
  // Given names and characters first, so an automatic one never takes them.
  colors.forEach((color, i) => {
    const name = color.name?.trim();
    if (name && !names.has(name.toLowerCase())) {
      names.add(name.toLowerCase());
      result[i].name = name;
    }
    if (color.char && isPictureChar(color.char) && !/[&<>"']/.test(color.char) && !chars.has(color.char)) {
      chars.add(color.char);
      result[i].char = color.char;
    }
  });
  colors.forEach((color, i) => {
    result[i].hex = color.hex;
    if (!result[i].name) {
      const preferred = color.hex === '#000000' ? 'black' : color.hex === '#ffffff' ? 'white' : i === 0 ? 'background' : `color${i}`;
      let name = preferred;
      for (let n = 2; names.has(name.toLowerCase()); n++) name = `${preferred}${n}`;
      names.add(name.toLowerCase());
      result[i].name = name;
    }
    if (!result[i].char) {
      const char = [i === 0 ? '.' : 'X', ...CHAR_POOL].find((ch) => !chars.has(ch));
      if (!char) throw new PbnError('Too many colors to give each a character');
      chars.add(char);
      result[i].char = char;
    }
  });
  return result;
}

function element(tag: string, value: string): string {
  return `<${tag}>${escape(value)}</${tag}>`;
}

function escape(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

// ===== A small, forgiving XML reader =====

interface XmlElement {
  name: string;
  attrs: Record<string, string>;
  children: Array<XmlElement | string>;
}

/**
 * Reads XML into elements and text. Names are lower-cased. It forgives what
 * hand-written files get wrong: a closing tag that matches nothing open is
 * ignored (the format's own sample closes `<clues>` with `</clue>`), and
 * elements left open are closed at the end.
 */
function parseXml(xml: string): XmlElement {
  const root: XmlElement = { name: '#document', attrs: {}, children: [] };
  const stack = [root];
  const top = () => stack[stack.length - 1];
  const addText = (text: string) => {
    if (text) top().children.push(decodeEntities(text));
  };
  let i = 0;
  while (i < xml.length) {
    const lt = xml.indexOf('<', i);
    if (lt < 0) {
      addText(xml.slice(i));
      break;
    }
    addText(xml.slice(i, lt));
    if (xml.startsWith('<!--', lt)) {
      i = skipPast(xml, '-->', lt + 4, 'comment');
    } else if (xml.startsWith('<![CDATA[', lt)) {
      const end = xml.indexOf(']]>', lt + 9);
      if (end < 0) throw new PbnError('The file has an unclosed CDATA section');
      top().children.push(xml.slice(lt + 9, end));
      i = end + 3;
    } else if (xml.startsWith('<?', lt)) {
      i = skipPast(xml, '?>', lt + 2, 'processing instruction');
    } else if (xml.startsWith('<!', lt)) {
      i = skipDeclaration(xml, lt + 2);
    } else if (xml[lt + 1] === '/') {
      const end = xml.indexOf('>', lt);
      if (end < 0) throw new PbnError('The file ends inside a tag');
      const name = xml.slice(lt + 2, end).trim().toLowerCase();
      for (let s = stack.length - 1; s > 0; s--) {
        if (stack[s].name === name) {
          stack.length = s;
          break;
        }
      }
      i = end + 1;
    } else {
      const tag = readStartTag(xml, lt);
      if (!tag) {
        addText('<');
        i = lt + 1;
        continue;
      }
      const el: XmlElement = { name: tag.name, attrs: tag.attrs, children: [] };
      top().children.push(el);
      if (!tag.selfClosing) stack.push(el);
      i = tag.end;
    }
  }
  return root;
}

function skipPast(xml: string, marker: string, from: number, what: string): number {
  const end = xml.indexOf(marker, from);
  if (end < 0) throw new PbnError(`The file has an unclosed ${what}`);
  return end + marker.length;
}

/** Skips `<!DOCTYPE …>`, including an internal subset in […]. */
function skipDeclaration(xml: string, from: number): number {
  let depth = 0;
  for (let j = from; j < xml.length; j++) {
    const ch = xml[j];
    if (ch === '"' || ch === "'") {
      const close = xml.indexOf(ch, j + 1);
      if (close < 0) break;
      j = close;
    } else if (ch === '[') depth++;
    else if (ch === ']') depth--;
    else if (ch === '>' && depth <= 0) return j + 1;
  }
  throw new PbnError('The file has an unclosed declaration');
}

function readStartTag(xml: string, lt: number): { name: string; attrs: Record<string, string>; selfClosing: boolean; end: number } | null {
  const nameMatch = /^[A-Za-z_][\w.:-]*/.exec(xml.slice(lt + 1, lt + 200));
  if (!nameMatch) return null;
  const attrs: Record<string, string> = {};
  let j = lt + 1 + nameMatch[0].length;
  while (j < xml.length) {
    while (/\s/.test(xml[j] ?? '')) j++;
    if (xml[j] === '>') return { name: nameMatch[0].toLowerCase(), attrs, selfClosing: false, end: j + 1 };
    if (xml.startsWith('/>', j)) return { name: nameMatch[0].toLowerCase(), attrs, selfClosing: true, end: j + 2 };
    const attr = /^([^\s=/>]+)\s*(?:=\s*("[^"]*"|'[^']*'|[^\s>]+))?/.exec(xml.slice(j, j + 4096));
    if (!attr) throw new PbnError('The file has a tag it can\'t read');
    const raw = attr[2] ?? '';
    attrs[attr[1].toLowerCase()] = decodeEntities(/^["']/.test(raw) ? raw.slice(1, -1) : raw);
    j += attr[0].length;
  }
  throw new PbnError('The file ends inside a tag');
}

/** Direct children of `el` named `name`. */
function childElements(el: XmlElement, name: string): XmlElement[] {
  return el.children.filter((c): c is XmlElement => typeof c !== 'string' && c.name === name);
}

/** Elements named `name` anywhere under `el`, without looking inside other puzzles. */
function descendants(el: XmlElement, name: string): XmlElement[] {
  const found: XmlElement[] = [];
  const visit = (node: XmlElement) => {
    for (const child of node.children) {
      if (typeof child === 'string') continue;
      if (child.name === name) found.push(child);
      if (child.name !== 'puzzle') visit(child);
    }
  };
  visit(el);
  return found;
}

/** The first `name` at the top of the document, or just below an unexpected wrapper. */
function findTop(root: XmlElement, name: string): XmlElement | undefined {
  return childElements(root, name)[0] ?? descendants(root, name)[0];
}

function childText(el: XmlElement, name: string): string | undefined {
  const child = childElements(el, name)[0];
  return child ? textOf(child) : undefined;
}

function textOf(el: XmlElement): string {
  return el.children.map((c) => (typeof c === 'string' ? c : textOf(c))).join('');
}

/** HTML's Latin-1 entity names, for code points 160 to 255 in order. */
const LATIN1_ENTITIES = ('nbsp iexcl cent pound curren yen brvbar sect uml copy ordf laquo not shy reg macr deg plusmn sup2 sup3 '
  + 'acute micro para middot cedil sup1 ordm raquo frac14 frac12 frac34 iquest Agrave Aacute Acirc Atilde Auml Aring AElig '
  + 'Ccedil Egrave Eacute Ecirc Euml Igrave Iacute Icirc Iuml ETH Ntilde Ograve Oacute Ocirc Otilde Ouml times Oslash Ugrave '
  + 'Uacute Ucirc Uuml Yacute THORN szlig agrave aacute acirc atilde auml aring aelig ccedil egrave eacute ecirc euml igrave '
  + 'iacute icirc iuml eth ntilde ograve oacute ocirc otilde ouml divide oslash ugrave uacute ucirc uuml yacute thorn yuml').split(' ');

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  ndash: '–', mdash: '—', lsquo: '‘', rsquo: '’', ldquo: '“', rdquo: '”',
  hellip: '…', bull: '•', trade: '™', euro: '€', dagger: '†', OElig: 'Œ', oelig: 'œ',
  ...Object.fromEntries(LATIN1_ENTITIES.map((name, i) => [name, String.fromCharCode(160 + i)])),
};

/** The format allows HTML's character entities; unknown ones are left as written. */
function decodeEntities(text: string): string {
  return text.replace(/&(#[xX][0-9a-fA-F]+|#\d+|[A-Za-z][A-Za-z0-9]*);/g, (match, entity: string) => {
    if (entity[0] === '#') {
      const code = entity[1] === 'x' || entity[1] === 'X' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : match;
    }
    return NAMED_ENTITIES[entity] ?? match;
  });
}
